import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useClerkAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const syncedRef = useRef(false);

  // Sync Clerk user to local state and ensure user exists in backend
  useEffect(() => {
    const syncUser = async () => {
      if (!clerkLoaded) return;
      
      if (isSignedIn && clerkUser) {
        // Prevent duplicate syncs
        if (syncedRef.current && user?.id === clerkUser.id) {
          setLoading(false);
          return;
        }
        
        try {
          // Get fresh token for API calls
          const token = await getToken({ skipCache: true });
          if (token) {
            localStorage.setItem('token', token);
          }
          
          // Map Clerk user to our user format
          const mappedUser = {
            id: clerkUser.id,
            email: clerkUser.primaryEmailAddress?.emailAddress || '',
            name: clerkUser.fullName || clerkUser.firstName || 'User',
            picture: clerkUser.imageUrl,
            is_guest: false,
          };
          
          setUser(mappedUser);
          localStorage.setItem('user', JSON.stringify(mappedUser));
          
          // Sync user to backend (create if doesn't exist) - only once
          if (!syncedRef.current) {
            try {
              await api.post('/auth/clerk-sync', {
                clerk_id: clerkUser.id,
                email: mappedUser.email,
                name: mappedUser.name,
                picture: mappedUser.picture,
              });
              console.log('User synced to backend successfully');
              syncedRef.current = true;
            } catch (error) {
              // User sync failed but don't block - might be token issue, will retry
              console.log('User sync to backend failed:', error.message);
            }
          }
        } catch (error) {
          console.error('Error during user sync:', error);
        }
      } else {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        syncedRef.current = false;
      }
      
      setLoading(false);
    };
    
    syncUser();
  }, [clerkLoaded, isSignedIn, clerkUser?.id]); // Only depend on clerkUser.id, not the whole object

  // Refresh token periodically (Clerk tokens expire in 60 seconds)
  useEffect(() => {
    if (!isSignedIn) return;
    
    const refreshToken = async () => {
      try {
        const token = await getToken({ skipCache: true });
        if (token) {
          localStorage.setItem('token', token);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    };
    
    // Refresh token every 50 seconds
    const interval = setInterval(refreshToken, 50000);
    return () => clearInterval(interval);
  }, [isSignedIn, getToken]);

  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      syncedRef.current = false;
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [signOut]);

  const value = {
    user,
    loading: !clerkLoaded || loading,
    isAuthenticated: isSignedIn && !!user,
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
